import { useCallback, useEffect, useState } from 'react';
import { ActivitiesPreview } from '@/entities/Activity/ui/ActivitiesPreview';
import Layout from '@/shared/Layout/ui/Layout';
import { ActivitiesItineraryProps } from '../domain/ItineraryPage.domain';
import * as S from './Itinerary.styles';
import { IActivityWithAvailability } from '@/common/domain/Merge.domain';
import ItineraryPageMeta from '../app/Meta';
import { Container } from '@/common/ui/containers/Container';
import { Preloader } from '@/common/ui/loaders/Preloader';
import Subscribe from '@/shared/Subscribe/ui/Subscribe';
import { WrapperSubscribe } from '@/screens/PartnersDetailPage/PartnersDetail.styles';
import dynamic from 'next/dynamic';
import { FeaturedActivitiesProps } from '@/shared/FeaturedActivities/ui/FeaturedActivities';
import { IRecommendsItem } from '@/shared/FeaturedActivities/ui/featuredActivitiesPreview/FeaturedActivitiesPreview.types';
import { getRecommends } from '@/shared/FeaturedActivities/service/ApiFeaturedActivities';
import { IActivityItem } from '@/common/service/api/Activity/Activity.domain';
import { useDateContext } from '@/common/app/contexts';
import HomePageLight from '@/shared/images/HeroImages/HomePageLight.jpg';
import URL from '@/common/app/utils/urlsUtils';
import { useChunksList } from '@/common/app/hooks/useChunksList';
import { BottomButton } from '@/common/ui/buttons/BottomButton/BottomButton';
import { useShowMore } from '@/common/service/hooks/useShowMore';
import { getTripDataById } from '@/shared/Analytics/GA4/helpers';

const FeaturedActivities = dynamic<FeaturedActivitiesProps>(
    () =>
        import('@/shared/FeaturedActivities/ui/FeaturedActivities').then(
            (module) => module.FeaturedActivities
        ),
    {
        ssr: false,
    }
);

const ItineraryPage = ({ itinerary, items, pageDestination }: ActivitiesItineraryProps) => {
    const { date } = useDateContext();

    const [recommends, setRecommends] = useState<IRecommendsItem[]>();

    const fetchRecommends = useCallback(async () => {
        try {
            if (date) {
                const resRecommends = await getRecommends({
                    destination_id: itinerary.destination_id,
                    category_id: itinerary.category_id,
                    from: date.from,
                    to: date.to,
                });

                return setRecommends(resRecommends);
            }
        } catch (error) {
            return setRecommends([]);
        }
    }, [itinerary.category_id, itinerary.destination_id, date]);

    useEffect(() => {
        fetchRecommends();
    }, [fetchRecommends]);

    const { isLoading, hasMoreTrips, showList, userImages, onScrollPage, firstLoading } =
        useChunksList(items as IActivityItem[], itinerary.id);

    const { showMoreTours, showMoreText, isLastItem } = useShowMore(
        hasMoreTrips,
        isLoading,
        showList.length,
        items.length
    );
    const item_list_id = typeof window !== 'undefined' ? window.location.pathname : '';

    return (
        <Layout>
            <ItineraryPageMeta
                itinerary={itinerary}
                img={
                    items[0]
                        ? `${URL.image}/activity/${items[0].id}/1080x1080/${items[0].slug}.jpg`
                        : HomePageLight.src
                }
            />
            <Container>
                <S.Title data-test-id="Itinerary_title">{itinerary.title}</S.Title>
                <S.Description>{itinerary.content}</S.Description>
                {items.length > 0 && firstLoading ? (
                    <Preloader minHeight={'calc(100vh - 140px)'} />
                ) : (
                    <>
                        <S.ItineraryContainer data-test-id="Itinerary_card_container">
                            {showList.map((activity, index) => {
                                const additionalData = getTripDataById(activity.id);
                                return (
                                    <ActivitiesPreview
                                        key={activity.id}
                                        activity={
                                            {
                                                ...activity,
                                                city: {
                                                    name: additionalData.item_category,
                                                    id: additionalData.item_category_id,
                                                },
                                                category: {
                                                    name: additionalData.item_category2,
                                                    id: additionalData.item_category2_id,
                                                },
                                                google: {
                                                    place_id: additionalData.location_id,
                                                },
                                            } as IActivityWithAvailability
                                        }
                                        position={index}
                                        listName={itinerary.title}
                                        userImages={userImages[`parent_${activity.id}`]}
                                        isLastItem={isLastItem(index)}
                                    />
                                );
                            })}
                        </S.ItineraryContainer>
                        {isLoading && <Preloader />}
                    </>
                )}
                {showMoreTours && (
                    <BottomButton title={showMoreText} onClick={onScrollPage} isTripsList />
                )}
            </Container>
            <FeaturedActivities
                activities={recommends || []}
                title={`Featured Activities in ${pageDestination.name}`}
                item_list_id={item_list_id}
                item_list_name={`Featured Activities in ${pageDestination.name}`}
                skipSkeleton
            />
            <WrapperSubscribe>
                <Subscribe />
            </WrapperSubscribe>
        </Layout>
    );
};

export { ItineraryPage };
export default ItineraryPage;
